import { useState } from "react";
import { X, DollarSign, Tag, Layers, ToggleRight, Star, Archive } from "lucide-react";
import { toast } from "sonner";

/**
 * Sticky bulk-edit modal used by AdminListingManager.
 *
 * Supports 6 field groups (Etsy-style ops):
 *   1. Price     — set / ±$ / ±%
 *   2. Sale      — set / ±$ / ±% / clear
 *   3. Category  — replace / clear
 *   4. Tags      — replace / add / remove (CustomProduct only, but harmless for RM)
 *   5. Status    — active/inactive/archive
 *   6. Featured  — on/off
 *   7. Stock     — in_stock / low_stock / out_of_stock (RM only field, safe no-op on custom)
 *
 * Emits ONE payload to the caller's `onApply(payload)` which routes to
 * either `adminProductsBulkUpdate` or `adminReadyMadeBulkUpdate`.
 */
export default function BulkEditModal({ open, selectedIds, mode, onClose, onApply, categoryOptions = [] }) {
  const [tab, setTab] = useState("price");
  const [priceOp, setPriceOp] = useState("set");
  const [priceValue, setPriceValue] = useState("");
  const [saleOp, setSaleOp] = useState("set");
  const [saleValue, setSaleValue] = useState("");
  const [catOp, setCatOp] = useState("replace");
  const [catValue, setCatValue] = useState("");
  const [tagsOp, setTagsOp] = useState("add");
  const [tagsValue, setTagsValue] = useState("");
  const [statusValue, setStatusValue] = useState("active");
  const [featuredValue, setFeaturedValue] = useState("true");
  const [stockValue, setStockValue] = useState("in_stock");
  const [saving, setSaving] = useState(false);

  if (!open) return null;

  const build = () => {
    if (tab === "price") return { price_op: priceOp, price_value: parseFloat(priceValue || 0) };
    if (tab === "sale") return saleOp === "clear"
      ? { sale_price_op: "clear" }
      : { sale_price_op: saleOp, sale_price_value: parseFloat(saleValue || 0) };
    if (tab === "category") return catOp === "clear"
      ? { category_op: "clear" }
      : { category_op: "replace", category_value: catValue.trim() };
    if (tab === "tags") return {
      tags_op: tagsOp,
      tags_value: tagsValue.split(",").map((t) => t.trim()).filter(Boolean),
    };
    if (tab === "status") return { set: { active: statusValue === "active", archived: statusValue === "archived" } };
    if (tab === "featured") return { set: { featured: featuredValue === "true" } };
    if (tab === "stock") return { set: { stock_status: stockValue } };
    return {};
  };

  const submit = async () => {
    const payload = { ids: selectedIds, ...build() };
    setSaving(true);
    try {
      await onApply(payload);
      onClose();
    } catch (e) {
      toast.error("Bulk edit failed: " + (e?.message || "unknown"));
    } finally {
      setSaving(false);
    }
  };

  const TABS = [
    { k: "price", l: "Price", icon: DollarSign },
    { k: "sale", l: "Sale Price", icon: DollarSign },
    { k: "category", l: "Category", icon: Layers },
    { k: "tags", l: "Tags", icon: Tag },
    { k: "status", l: "Status", icon: ToggleRight },
    { k: "featured", l: "Featured", icon: Star },
    ...(mode === "ready-made" ? [{ k: "stock", l: "Stock", icon: Archive }] : []),
  ];

  return (
    <div
      className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="bulk-edit-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="surface-card max-w-2xl w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="p-6 border-b border-[#222] flex justify-between items-center">
          <div>
            <h3 className="font-display text-3xl uppercase">Bulk Edit</h3>
            <div className="font-mono text-[10px] tracking-widest text-zinc-500 mt-1">
              {selectedIds.length} SELECTED · {mode.toUpperCase()}
            </div>
          </div>
          <button onClick={onClose} data-testid="bulk-edit-close"><X size={18} /></button>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-1 p-3 border-b border-[#222]" data-testid="bulk-edit-tabs">
          {TABS.map(({ k, l, icon: Icon }) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              data-testid={`bulk-tab-${k}`}
              className={`px-3 py-2 font-mono text-[10px] tracking-widest border flex items-center gap-1 ${
                tab === k
                  ? "border-[var(--tc-red)] bg-[var(--tc-red)]/10 text-white"
                  : "border-[#222] text-zinc-400 hover:border-zinc-500"
              }`}
            >
              <Icon size={11} /> {l.toUpperCase()}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="p-6 space-y-4">
          {tab === "price" && (
            <div className="space-y-3" data-testid="bulk-edit-price">
              <label className="font-mono text-[10px] tracking-widest text-zinc-500">OPERATION</label>
              <select
                value={priceOp}
                onChange={(e) => setPriceOp(e.target.value)}
                className="w-full bg-black border border-[#222] px-3 py-2 font-mono text-xs"
                data-testid="bulk-price-op"
              >
                <option value="set">Set to fixed price</option>
                <option value="inc_amt">Increase by $ amount</option>
                <option value="dec_amt">Decrease by $ amount</option>
                <option value="inc_pct">Increase by %</option>
                <option value="dec_pct">Decrease by %</option>
              </select>
              <label className="font-mono text-[10px] tracking-widest text-zinc-500 block">VALUE</label>
              <input
                type="number"
                step="0.01"
                value={priceValue}
                onChange={(e) => setPriceValue(e.target.value)}
                placeholder={priceOp.includes("pct") ? "e.g. 10 for 10%" : "e.g. 29.99"}
                className="w-full bg-black border border-[#222] px-3 py-2 font-mono text-xs"
                data-testid="bulk-price-value"
              />
            </div>
          )}
          {tab === "sale" && (
            <div className="space-y-3" data-testid="bulk-edit-sale">
              <label className="font-mono text-[10px] tracking-widest text-zinc-500">OPERATION</label>
              <select
                value={saleOp}
                onChange={(e) => setSaleOp(e.target.value)}
                className="w-full bg-black border border-[#222] px-3 py-2 font-mono text-xs"
                data-testid="bulk-sale-op"
              >
                <option value="set">Set sale price</option>
                <option value="inc_amt">Increase sale by $</option>
                <option value="dec_amt">Decrease sale by $</option>
                <option value="inc_pct">Increase sale by %</option>
                <option value="dec_pct">Decrease sale by %</option>
                <option value="clear">Remove sale price</option>
              </select>
              {saleOp !== "clear" && (
                <>
                  <label className="font-mono text-[10px] tracking-widest text-zinc-500 block">VALUE</label>
                  <input
                    type="number"
                    step="0.01"
                    value={saleValue}
                    onChange={(e) => setSaleValue(e.target.value)}
                    placeholder={saleOp.includes("pct") ? "e.g. 15 for 15%" : "e.g. 24.99"}
                    className="w-full bg-black border border-[#222] px-3 py-2 font-mono text-xs"
                    data-testid="bulk-sale-value"
                  />
                </>
              )}
            </div>
          )}
          {tab === "category" && (
            <div className="space-y-3" data-testid="bulk-edit-category">
              <label className="font-mono text-[10px] tracking-widest text-zinc-500">OPERATION</label>
              <select
                value={catOp}
                onChange={(e) => setCatOp(e.target.value)}
                className="w-full bg-black border border-[#222] px-3 py-2 font-mono text-xs"
                data-testid="bulk-cat-op"
              >
                <option value="replace">Replace category</option>
                <option value="clear">Clear category</option>
              </select>
              {catOp === "replace" && (
                <>
                  <label className="font-mono text-[10px] tracking-widest text-zinc-500 block">NEW CATEGORY</label>
                  {categoryOptions.length > 0 && (
                    <select
                      value={categoryOptions.includes(catValue) ? catValue : ""}
                      onChange={(e) => setCatValue(e.target.value)}
                      className="w-full bg-black border border-[#222] px-3 py-2 font-mono text-xs mb-2"
                      data-testid="bulk-cat-select"
                    >
                      <option value="">— Pick existing category —</option>
                      {categoryOptions.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  )}
                  <input
                    type="text"
                    value={catValue}
                    onChange={(e) => setCatValue(e.target.value)}
                    placeholder={categoryOptions.length > 0 ? "…or type a new category" : "e.g. tshirt / hoodie / hat"}
                    className="w-full bg-black border border-[#222] px-3 py-2 font-mono text-xs"
                    data-testid="bulk-cat-value"
                  />
                </>
              )}
            </div>
          )}
          {tab === "tags" && (
            <div className="space-y-3" data-testid="bulk-edit-tags">
              <label className="font-mono text-[10px] tracking-widest text-zinc-500">OPERATION</label>
              <select
                value={tagsOp}
                onChange={(e) => setTagsOp(e.target.value)}
                className="w-full bg-black border border-[#222] px-3 py-2 font-mono text-xs"
                data-testid="bulk-tags-op"
              >
                <option value="add">Add tags</option>
                <option value="remove">Remove tags</option>
                <option value="replace">Replace tags (overwrite)</option>
              </select>
              <label className="font-mono text-[10px] tracking-widest text-zinc-500 block">TAGS (comma-separated)</label>
              <input
                type="text"
                value={tagsValue}
                onChange={(e) => setTagsValue(e.target.value)}
                placeholder="e.g. summer, sale, unisex"
                className="w-full bg-black border border-[#222] px-3 py-2 font-mono text-xs"
                data-testid="bulk-tags-value"
              />
            </div>
          )}
          {tab === "status" && (
            <div className="space-y-3" data-testid="bulk-edit-status">
              <label className="font-mono text-[10px] tracking-widest text-zinc-500">NEW STATUS</label>
              <select
                value={statusValue}
                onChange={(e) => setStatusValue(e.target.value)}
                className="w-full bg-black border border-[#222] px-3 py-2 font-mono text-xs"
                data-testid="bulk-status-value"
              >
                <option value="active">Active (live)</option>
                <option value="inactive">Inactive (hidden)</option>
                <option value="archived">Archived (hidden + archived)</option>
              </select>
            </div>
          )}
          {tab === "featured" && (
            <div className="space-y-3" data-testid="bulk-edit-featured">
              <label className="font-mono text-[10px] tracking-widest text-zinc-500">FEATURED</label>
              <select
                value={featuredValue}
                onChange={(e) => setFeaturedValue(e.target.value)}
                className="w-full bg-black border border-[#222] px-3 py-2 font-mono text-xs"
                data-testid="bulk-featured-value"
              >
                <option value="true">Mark as featured</option>
                <option value="false">Unmark featured</option>
              </select>
            </div>
          )}
          {tab === "stock" && (
            <div className="space-y-3" data-testid="bulk-edit-stock">
              <label className="font-mono text-[10px] tracking-widest text-zinc-500">STOCK STATUS</label>
              <select
                value={stockValue}
                onChange={(e) => setStockValue(e.target.value)}
                className="w-full bg-black border border-[#222] px-3 py-2 font-mono text-xs"
                data-testid="bulk-stock-value"
              >
                <option value="in_stock">In stock</option>
                <option value="low_stock">Low stock</option>
                <option value="out_of_stock">Out of stock</option>
              </select>
            </div>
          )}
        </div>

        <div className="p-6 border-t border-[#222] flex justify-between items-center">
          <div className="font-mono text-[10px] text-zinc-500">
            APPLIES TO {selectedIds.length} PRODUCT{selectedIds.length === 1 ? "" : "S"}
          </div>
          <div className="flex gap-3">
            <button onClick={onClose} className="btn-tc-secondary">Cancel</button>
            <button
              onClick={submit}
              disabled={saving}
              className="btn-tc-primary disabled:opacity-50"
              data-testid="bulk-edit-apply"
            >
              {saving ? "Applying…" : "Apply"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
